import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import Button from "../ui/Button";
import "react-quill/dist/quill.snow.css";

interface CreateUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDraft: (title: string, content: string) => void;
  onPublish: (title: string, content: string) => void;
  loading?: boolean;
  mode?: "create" | "edit";
  initialContent?: string;
  initialTitle?: string;
  updateId?: number;
  currentStatus?: "published" | "draft";
}

export default function CreateUpdateModal({
  isOpen,
  onClose,
  onDraft,
  onPublish,
  loading = false,
  mode = "create",
  initialContent = "",
  initialTitle = "",
  updateId,
  currentStatus,
}: CreateUpdateModalProps) {
  const [content, setContent] = useState(initialContent);
  const [title, setTitle] = useState(initialTitle);

  // Update content and title when initial values change (e.g., when switching from create to edit)
  useEffect(() => {
    setContent(initialContent);
    setTitle(initialTitle);
  }, [initialContent, initialTitle, isOpen]);

  const handleDraft = () => {
    onDraft(title, content);
  };

  const handlePublish = () => {
    onPublish(title, content);
  };

  const handleClose = () => {
    if (mode === "create") {
      setContent("");
      setTitle("");
    }
    onClose();
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "image",
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={() => {}}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
    >
      <Dialog.Panel
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <Dialog.Title className="px-6 pt-6 pb-4 text-lg font-semibold text-black border-b border-neutral-200">
          {mode === "create" ? "What's New in Cadence" : "What's New in Cadence - Edit Update"}
        </Dialog.Title>
        <div className="flex-1 px-6 py-4 overflow-y-auto scrollbar-thin">
          <div className="mb-4">
            <label htmlFor="update-title" className="block mb-2 text-sm font-medium text-neutral-700">
              Title
            </label>
            <input
              id="update-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter update title..."
              className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
            />
          </div>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            className="h-96"
            placeholder="Write your update here..."
          />
        </div>

        {/*
          Three tiers, so the footer reads as a hierarchy rather than three
          equally-weighted buttons: Publish is the commitment, Save Draft the
          supporting brand action, Cancel the neutral way out.
        */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t rounded-b-lg border-neutral-200 bg-neutral-50">
          <Button
            variant="neutral"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          {(mode === "create" || currentStatus === "draft") && (
            <Button
              variant="secondary"
              onClick={handleDraft}
              disabled={loading || !content.trim() || !title.trim()}
            >
              {loading ? "Saving..." : "Save Draft"}
            </Button>
          )}
          <Button
            onClick={handlePublish}
            disabled={loading || !content.trim() || !title.trim()}
          >
            {loading ? "Publishing..." : "Publish"}
          </Button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}
